/* eslint-disable @typescript-eslint/no-unused-vars */
import { removedCellClassName } from '@helpers';
import { Table } from '@molecules/Table';
import { renderDateFilter } from '@molecules/Table/filters';
import { Box, Button, Grid, Link, Tooltip, Typography, Menu, MenuItem } from '@mui/material';
import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import {
  CaseState,
  caseStateMapper,
  ComplianceStatus as ComplianceStatusEnum,
  complianceStatusMapper,
  platformMapper,
  PostType,
  postTypeMapper,
  SocialMediaCategory,
  socialMediaCategoryMapper,
  SocialMediaLink,
  SocialMediaPlatform,
} from '@services/types/api';
import React, { useCallback, useMemo, useState } from 'react';
import { DATE_FORMAT } from '@constants/constants';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
  EditSocialMediaLinkInput,
  editSocialMediaLinkInputSchema,
  useEditSocialMediaLinks,
} from '../api/edit-social-links';
import AddButton from './add-social-media-link';
import CaseStateCell from './case-state-cell';
import ComplianceStatus from './compliance-status';
import PlatformInfoCell from './platform-info-cell';
import './styles.css';
import UploadButton from './upload-button';
import { TextField } from '@atoms';
import DropdownController from './dropdown-controller';
import {
  errorMessage,
  FilterItem,
  successMessage,
  TableSelectType,
  warningMessage,
} from '@services';
import { useEnforceSocialMedia } from '../api/enforce-social-media';
import { ConfirmModal } from '@organisms';
import { useConfirmationModal, useModalState } from '@hooks';
import { HistoryIcon } from '@atoms/Icons/HistoryIcon';
import { DatePickerStyled, defaultFormControlStyle, defaultStyleIcon } from './styles';
import { categoryOptions, platformOptions } from './constants';
import isEqual from 'lodash.isequal';
import { useDeleteSocialMediaLinks } from '../api/delete-social-links';
import { DeleteModalIcon } from '@atoms/Icons/DeleteModalIcon';
import { LoadingButton } from '@mui/lab';
import EnforceButton from './enforce-button';
import EditButton from './edit-button';
import CameraIcon from '@atoms/Icons/CameraIcon';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import EditMultipleSocialLink, { IGetColumnData } from './edit-multiple-social-link';
import {
  ConfirmEnforceModal,
  EditNoteModal,
  SocialMediaHistoryModal,
  UploadEvidenceModal,
} from '@organisms/Modals';
import MainUrlLink from './main-url-link';
import ColumnFilter from './column-filter';
import ExportButton from './export-button';
import InteractButton from '@atoms/Button/InteractButton';
import QueryKeys from '@constants/query-keys';
import AutoSubmissionModal from './auto-submission-modal';
import { checkIfHasAutoSubmissionForm } from '../utils/auto-submission';

const CATEGORY_MIXED = 'mixed';

const SEARCH_OPTIONS = [
  { label: 'AccountName', value: 'accountName' },
  { label: 'Main URL', value: 'mainUrl' },
  { label: 'Page/Account URL', value: 'accountUrl' },
  { label: 'Notes', value: 'note' },
];

const FILTERS_LIST: FilterItem[] = [
  {
    name: 'Detection Date',
    key: 'detectionStartDate',
    resetFields: ['detectionStartDate', 'detectionEndDate'],
    getFilter: renderDateFilter({
      label: 'Detection Date',
      key: 'detectionStartDate',
      resetFields: ['detectionStartDate', 'detectionEndDate'],
    }),
  },
  {
    name: 'Enforcement Date',
    key: 'enforcementStartDate',
    resetFields: ['enforcementStartDate', 'enforcementEndDate'],
    getFilter: renderDateFilter({
      label: 'Enforcement Date',
      key: 'enforcementStartDate',
      resetFields: ['enforcementStartDate', 'enforcementEndDate'],
    }),
  },
];

const getColumns = ({
  control,
  errors,
  rowEditing,
  setEditingNote,
  setOpenNoteModal,
  setIsOpenEvidenceModal,
  setOpenHistoryModal,
  setSelectedModel,
  handleClickSaveSingular,
}: IGetColumnData & {
  setEditingNote: (note: string) => void;
  setOpenNoteModal: (isOpen: boolean) => void;
  setIsOpenEvidenceModal: (isOpen: boolean) => void;
  setOpenHistoryModal: (isOpen: boolean) => void;
  setSelectedModel: ({ modelId, id }: { modelId: string; id: string }) => void;
  handleClickSaveSingular: () => void;
}): GridColDef<SocialMediaLink>[] => {
  return [
    {
      field: 'index',
      headerName: '',
      maxWidth: 60,
      cellClassName: removedCellClassName,
      renderCell: ({ value }) => {
        return <Typography>{value}</Typography>;
      },
      sortable: false,
      filterable: false,
      resizable: false,
    },
    {
      field: 'platform',
      headerName: 'Platform',
      minWidth: 200,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.platform,
      renderHeader: (params) => (
        <Box display="flex" alignItems="center" fontWeight={700} gap={1}>
          <span>{params.colDef.headerName}</span>
          <ColumnFilter paramKey="platform" />
        </Box>
      ),
      renderCell: ({ row: { platform, detectionDate, id } }) => (
        <>
          {rowEditing && id === rowEditing.id ? (
            <DropdownController
              name="platform"
              sx={{ ...defaultFormControlStyle }}
              control={control}
              defaultValue={platform}
              options={platformOptions}
              getOptionLabel={(option) =>
                platformMapper[option as SocialMediaPlatform] || (option as string)
              }
              errors={errors.platform}
              disableClearable
            />
          ) : (
            <PlatformInfoCell platform={platform} date={detectionDate} />
          )}
        </>
      ),
      sortable: false,
      filterable: true,
    },
    {
      field: 'accountUrl',
      headerName: 'Page/Account URL',
      minWidth: 200,
      sortable: false,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.accountUrl,
      renderCell: ({ row: { accountUrl } }) => (
        <Tooltip title={accountUrl}>
          <Link
            className="ellipsis"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              maxWidth: '100%',
            }}
            href={accountUrl}
            target="_blank"
          >
            {accountUrl}
          </Link>
        </Tooltip>
      ),
      filterable: false,
    },
    {
      field: 'mainUrl',
      headerName: 'Main URL',
      minWidth: 200,
      sortable: false,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.mainUrl,
      renderCell: ({ row: { mainUrl, id, modelId } }) => (
        <MainUrlLink url={mainUrl} id={id} modelId={modelId} />
      ),
      filterable: false,
    },
    {
      field: 'accountName',
      headerName: 'Account Name',
      minWidth: 180,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.accountName,
      renderCell: ({ row: { accountName, id } }) => (
        <>
          {rowEditing && id === rowEditing.id ? (
            <TextField
              sx={{
                ...defaultFormControlStyle,
                '& .MuiOutlinedInput-input': {
                  padding: '8px 12px !important',
                },
              }}
              name="accountName"
              control={control}
              defaultValue={accountName}
              fullWidth
              placeholder="Insert Name"
              error={!!errors.accountName}
              helperText={null}
              type="text"
              onKeyDown={(e) => {
                if (
                  e.code === 'Space' ||
                  e.code === 'ArrowRight' ||
                  e.code === 'ArrowLeft' ||
                  e.key === ' '
                ) {
                  e.stopPropagation();
                }
              }}
            />
          ) : (
            <Tooltip title={accountName}>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                  maxWidth: '100%',
                }}
              >
                {accountName}
              </Box>
            </Tooltip>
          )}
        </>
      ),
      filterable: false,
    },
    {
      field: 'category',
      headerName: 'Category',
      minWidth: 180,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.category,
      renderHeader: (params) => (
        <Box
          display="flex"
          alignItems="center"
          fontWeight={700}
          gap={1}
          onClick={(e) => e.stopPropagation()}
        >
          <span>{params.colDef.headerName}</span>
          <ColumnFilter paramKey="category" />
        </Box>
      ),
      renderCell: ({ row: { category, id } }) => (
        <>
          {rowEditing && id === rowEditing.id ? (
            <DropdownController
              name="category"
              sx={{ ...defaultFormControlStyle }}
              control={control}
              defaultValue={category}
              options={categoryOptions}
              getOptionLabel={(option) =>
                socialMediaCategoryMapper[option as SocialMediaCategory] || (option as string)
              }
              errors={errors.category}
              disableClearable
            />
          ) : (
            <p>{socialMediaCategoryMapper[category]}</p>
          )}
        </>
      ),
      filterable: false,
    },
    {
      field: 'postType',
      headerName: 'Case Type',
      minWidth: 180,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.postType,
      renderHeader: (params) => (
        <Box
          display="flex"
          alignItems="center"
          fontWeight={700}
          gap={1}
          onClick={(e) => e.stopPropagation()}
        >
          <span>{params.colDef.headerName}</span>
          <ColumnFilter paramKey="postType" />
        </Box>
      ),
      renderCell: ({ row: { postType, id } }) => (
        <>
          {rowEditing && id === rowEditing.id ? (
            <DropdownController
              name="postType"
              sx={{ ...defaultFormControlStyle }}
              control={control}
              defaultValue={postType}
              options={Object.values(PostType)}
              getOptionLabel={(option) => postTypeMapper[option as PostType] || (option as string)}
              errors={errors.postType}
              disableClearable
            />
          ) : (
            <p>{postTypeMapper[postType]}</p>
          )}
        </>
      ),
      filterable: false,
    },
    {
      field: 'caseState',
      headerName: 'Case Status',
      minWidth: 190,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.caseState,
      renderHeader: (params) => (
        <Box
          display="flex"
          alignItems="center"
          fontWeight={700}
          gap={1}
          onClick={(e) => e.stopPropagation()}
        >
          <span>{params.colDef.headerName}</span>
          <ColumnFilter paramKey="caseState" />
        </Box>
      ),
      renderCell: ({ row: { caseState, id } }) => (
        <>
          {rowEditing && id === rowEditing.id ? (
            <DropdownController
              name="caseState"
              sx={{ ...defaultFormControlStyle }}
              control={control}
              defaultValue={caseState}
              options={Object.values(CaseState)}
              getOptionLabel={(option) =>
                caseStateMapper[option as CaseState] || (option as string)
              }
              errors={errors.caseState}
              disableClearable
            />
          ) : (
            <CaseStateCell state={caseState} />
          )}
        </>
      ),
      filterable: false,
    },
    {
      field: 'complianceStatus',
      headerName: 'Compliance Status',
      minWidth: 240,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.complianceStatus,
      renderHeader: (params) => (
        <Box
          display="flex"
          alignItems="center"
          fontWeight={700}
          gap={1}
          onClick={(e) => e.stopPropagation()}
        >
          <span>{params.colDef.headerName}</span>
          <ColumnFilter paramKey="complianceStatus" />
        </Box>
      ),
      renderCell: ({ row: { complianceStatus, id } }) => (
        <>
          {rowEditing && id === rowEditing.id ? (
            <DropdownController
              name="complianceStatus"
              sx={{ ...defaultFormControlStyle }}
              control={control}
              defaultValue={complianceStatus || ''}
              options={['', ...Object.values(ComplianceStatusEnum)]}
              getOptionLabel={(option) =>
                option
                  ? complianceStatusMapper[option as ComplianceStatusEnum] || (option as string)
                  : 'N/A'
              }
              errors={errors.complianceStatus}
              disableClearable
            />
          ) : (
            <ComplianceStatus status={complianceStatus} />
          )}
        </>
      ),
      filterable: false,
    },
    {
      field: 'detectionDate',
      headerName: 'Detection Date',
      minWidth: 190,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.lastEnforcementDate,
      renderCell: ({ row }) =>
        rowEditing && row.id === rowEditing.id ? (
          <Controller
            name="detectionDate"
            control={control}
            defaultValue={row.detectionDate}
            render={({ field: { value, ...others } }) => (
              <DatePickerStyled
                {...others}
                sx={{ ...defaultFormControlStyle }}
                selected={value ? new Date(value) : null}
                calendarStartDay={1}
                monthsShown={1}
                placeholderText="DD.MM.YYYY"
                dateFormat="dd.MM.yyyy"
                selectsRange={false}
                isClearable={false}
                onKeyDown={(e) => {
                  e.preventDefault();
                }}
              />
            )}
          />
        ) : (
          <Box>
            <p>{row.detectionDate ? dayjs(row.detectionDate).format(DATE_FORMAT) : 'N/A'}</p>
          </Box>
        ),
      filterable: false,
    },
    {
      field: 'enforcementDate',
      headerName: 'Enforcement Date',
      minWidth: 200,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.lastEnforcementDate,
      renderCell: ({ row }) => (
        <Box>
          <p>
            {row.lastEnforcementDate ? dayjs(row.lastEnforcementDate).format(DATE_FORMAT) : 'N/A'}
          </p>
        </Box>
      ),
      filterable: false,
    },
    {
      field: 'note',
      headerName: 'Notes',
      minWidth: 150,
      flex: 1,
      filterable: false,
      sortable: false,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.note,
      renderCell: ({ row: { note, id } }) => (
        <>
          {rowEditing && id === rowEditing.id ? (
            <Typography
              sx={{
                ...defaultFormControlStyle,
                width: 'fit-content',
                minWidth: '100px',
                height: '35px',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                cursor: 'pointer',
                border: '1px solid #E0E3E7',
                padding: '2px',
                paddingLeft: '5px',
              }}
              onClick={() => {
                setEditingNote(rowEditing && id === rowEditing.id ? rowEditing.note : note);
                setOpenNoteModal(true);
              }}
            >
              {rowEditing && id === rowEditing.id ? rowEditing.note : note}
            </Typography>
          ) : (
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{note}</span>}>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                  maxWidth: '100%',
                }}
              >
                {note}
              </Box>
            </Tooltip>
          )}
        </>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 100,
      flex: 1,
      filterable: false,
      sortable: false,
      cellClassName: removedCellClassName,
      renderCell: ({ row: { modelId, id } }) => (
        <Box display="flex" justifyContent="start" alignItems="center" gap="4px">
          <Tooltip title="Save">
            <SaveIcon
              sx={
                rowEditing && id === rowEditing.id
                  ? { color: '#9DD049', cursor: 'pointer' }
                  : { ...defaultStyleIcon, cursor: 'not-allowed' }
              }
              onClick={rowEditing && id === rowEditing.id ? handleClickSaveSingular : () => {}}
            />
          </Tooltip>
          <Tooltip title="Show evidences">
            <Box display="flex">
              <CameraIcon
                sx={{
                  ...defaultStyleIcon,
                }}
                cursor="pointer"
                onClick={() => {
                  setSelectedModel({ modelId, id });
                  setIsOpenEvidenceModal(true);
                }}
              />
            </Box>
          </Tooltip>
          <Tooltip title="Show edit history">
            <Box display="flex">
              <HistoryIcon
                sx={{
                  ...defaultStyleIcon,
                }}
                cursor="pointer"
                width={15}
                height={17}
                onClick={() => {
                  setSelectedModel({ modelId, id });
                  setOpenHistoryModal(true);
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      ),
      resizable: false,
    },
  ];
};

const sxButtonConfirmModal = {
  minWidth: '116px',
  borderRadius: '10px',
  lineHeight: '20px',
  paddingY: '10px',
  height: 40,
};

const SocialMediaList = () => {
  const { assetId } = useParams();

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [selectedRows, setSelectedRows] = useState<SocialMediaLink[]>([]);
  const [rowEditing, setRowEditing] = useState<SocialMediaLink | null>(null);
  const [autoSubmissionLinks, setAutoSubmissionLinks] = useState<SocialMediaLink[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editMode, setEditMode] = useState<boolean>(false);

  const [openNoteModal, setOpenNoteModal] = useState<boolean>(false);
  const [openEnforceModal, setOpenEnforceModal] = useState<boolean>(false);
  const [openHistoryModal, setOpenHistoryModal] = useState<boolean>(false);
  const [isOpenEvidenceModal, setIsOpenEvidenceModal] = useState<boolean>(false);
  const [isOpenAutoSubmissionModal, , openAutoSubmissionModal, closeAutoSubmissionModal] =
    useModalState(false);

  const [editingNote, setEditingNote] = useState<string>('');
  const [resetSelection, setResetSelection] = useState(false);
  const [selectedModel, setSelectedModel] = useState<{ modelId: string; id: string }>({
    modelId: '',
    id: '',
  });
  const [uploadMenuAnchorEl, setUploadMenuAnchorEl] = useState<null | HTMLElement>(null);

  const {
    control,
    getValues,
    reset,
    formState: { errors },
  } = useForm<EditSocialMediaLinkInput>({
    resolver: yupResolver(editSocialMediaLinkInputSchema),
    mode: 'onChange',
  });

  const deleteSocialMediaLinksMutation = useDeleteSocialMediaLinks({
    mutationConfig: {
      onSuccess: () => {
        successMessage('Social Media Link has been deleted!');
        clearData();
      },
      onError: () => {
        errorMessage('Failed to delete Social Media Link');
      },
    },
  });

  const handleDeleteSocialLink = useCallback(async () => {
    if (!selectedRowIds || !selectedRowIds.length || !assetId) return;
    const payload = {
      socialMediaLinkIds: [...selectedRowIds],
      modelId: assetId,
    };
    await deleteSocialMediaLinksMutation.mutateAsync(payload);
  }, [deleteSocialMediaLinksMutation, selectedRowIds, assetId]);

  const handleOnSubmit = useCallback(async () => {
    if (isDeleting) {
      await handleDeleteSocialLink();
      return;
    }
    if (!rowEditing) return;
    setRowEditing(null);
    reset();
  }, [rowEditing, reset, isDeleting, handleDeleteSocialLink]);

  const [open, onOpen, onCancel, onSubmit] = useConfirmationModal(false, handleOnSubmit);

  const editSocialMediaLinksMutation = useEditSocialMediaLinks({
    mutationConfig: {
      onSuccess: () => {
        successMessage('Social Media Link has been edited!');
        clearData();
      },
      onError: () => {
        errorMessage('Failed to edit Social Media Link');
      },
    },
  });

  const handleClickSaveSingular = useCallback(() => {
    if (!rowEditing || Object.keys(errors).length !== 0) return;
    if (!assetId) {
      errorMessage('Model could not be found');
      return;
    }
    const { lastEnforcementDate, id, note } = rowEditing;
    const payload = {
      ...getValues(),
      complianceStatus: getValues().complianceStatus || null,
      lastEnforcementDate: lastEnforcementDate,
      note,
    };
    editSocialMediaLinksMutation.mutate({ data: payload, modelId: assetId, id });
  }, [rowEditing, errors, assetId, getValues, editSocialMediaLinksMutation]);

  const handleUploadMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUploadMenuAnchorEl(event.currentTarget);
  };

  const handleClickCancel = () => {
    setEditMode(false);
    clearData();
  };

  const handleClickDelete = () => {
    onOpen();
    setIsDeleting(true);
  };

  const clearData = useCallback(() => {
    setRowEditing(null);
    setSelectedRowIds([]);
    setSelectedRows([]);
    setAutoSubmissionLinks([]);
    setEditingNote('');
    setResetSelection(true);
    closeAutoSubmissionModal();

    setTimeout(() => {
      setResetSelection(false);
    }, 0);

    reset();

    if (isDeleting) setIsDeleting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, isDeleting]);

  const columns: GridColDef<SocialMediaLink>[] = useMemo(
    () =>
      getColumns({
        control,
        errors,
        rowEditing: rowEditing,
        setEditingNote,
        setOpenNoteModal,
        setIsOpenEvidenceModal,
        setOpenHistoryModal,
        setSelectedModel,
        handleClickSaveSingular,
      }),
    [
      control,
      errors,
      rowEditing,
      setEditingNote,
      setOpenNoteModal,
      setOpenHistoryModal,
      setSelectedModel,
      handleClickSaveSingular,
    ],
  );

  const getSelectedMultipleValue = useCallback(
    (items: SocialMediaLink[], selectedItemsId: string[]) => {
      const selectedItems = items.filter((item) =>
        selectedItemsId.includes((item as { id: string })?.id),
      );

      if (!isEqual(selectedRows, selectedItems)) {
        setSelectedRows(selectedItems);
      }

      const uniqCategories = selectedItems.reduce(
        (statuses: string[], { category }) =>
          statuses.includes(category) ? statuses : [...statuses, category],
        [],
      );
      const category = uniqCategories.length > 1 ? CATEGORY_MIXED : uniqCategories[0];

      return { status: category };
    },
    [selectedRows],
  );

  const handleClickEditSingleRow = useCallback(
    (row: SocialMediaLink) => {
      if (!rowEditing || rowEditing.id !== row.id) {
        setRowEditing(row);
        reset();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowEditing],
  );

  const getSelectedValue = <T extends { id: string }>(_items: T[], selectedItemsId: string[]) => {
    if (!isEqual(selectedRowIds, selectedItemsId)) {
      setSelectedRowIds(selectedItemsId);
    }

    const selectedItems = _items.filter((item) => selectedItemsId.includes(item.id));

    if (selectedItems.length === 1) {
      const selectedItem = selectedItems[0];
      if (!rowEditing || rowEditing.id !== selectedItem.id) {
        handleClickEditSingleRow(selectedItem as unknown as SocialMediaLink);
      }
    } else {
      setRowEditing(null);
    }

    return '';
  };

  const handleCancelConfirmModal = () => {
    onCancel();
    if (isDeleting) {
      setTimeout(() => {
        setIsDeleting(false);
      }, 250);
    }
  };

  const handleNoteUpdate = (newNote: string) => {
    if (rowEditing) {
      setRowEditing({ ...rowEditing, note: newNote });
    }
  };

  const enforceSocialMediaMutation = useEnforceSocialMedia({
    mutationConfig: {
      onSuccess: () => {
        successMessage('Social Media Link has been enforced!');
        setOpenEnforceModal(false);
        clearData();
      },
      onError: () => {
        setOpenEnforceModal(false);
        errorMessage('Failed to enforce Social Media Link');
      },
    },
  });

  const onClickEnforce = () => {
    const invalidAutoSubmissionForms: {
      category: SocialMediaCategory;
      platform: SocialMediaPlatform;
    }[] = [];

    const validAutoSubmissionForms = Array.from(
      new Set(
        selectedRows
          .filter((item) => {
            if (checkIfHasAutoSubmissionForm(item.category, item.platform)) {
              return true;
            }

            invalidAutoSubmissionForms.push({
              category: item.category,
              platform: item.platform,
            });
            return false;
          })
          .map((item) => ({ category: item.category, platform: item.platform }))
          .map((item) => JSON.stringify(item)),
      ),
    ).map((item) => JSON.parse(item));

    if (invalidAutoSubmissionForms.length > 0) {
      warningMessage(
        `We don't support auto submission for this category + platform yet.\n${invalidAutoSubmissionForms
          .map(({ category, platform }) => `- ${category} + ${platform}`)
          .join('\n')}\nSorry for this inconvenience. We'll improve it in the future.`,
      );
    }

    if (validAutoSubmissionForms.length > 0) {
      openAutoSubmissionModal();
      // TODO: temporarily set one kind of category + platform
      setAutoSubmissionLinks(
        selectedRows.filter(
          (item) =>
            item.category === validAutoSubmissionForms[0].category &&
            item.platform === validAutoSubmissionForms[0].platform,
        ),
      );

      if (validAutoSubmissionForms.length > 1) {
        warningMessage(
          "We're only able to enforce one category + platform at a time. Sorry for this inconvenience. We'll improve it in the future.",
        );
      }
      return;
    }

    setOpenEnforceModal(true);
  };

  const handleEnforce = () => {
    const ids = selectedRows.map((item) => item.id);
    if (!assetId) return;
    enforceSocialMediaMutation.mutate({ data: { ids }, modelId: assetId });
  };

  return (
    <>
      <Table<SocialMediaLink>
        resetSelection={resetSelection}
        searchPlaceholder="Search for (incl Regex)"
        queryName={QueryKeys.socialMediaLinks}
        listBoxClassName="social-media-container__table-search__list-box"
        popperClassName="social-media-container__table-search__popper"
        apiRoute={`/admin/models/${assetId}/social-media-links`}
        defaultQueries={{ size: 50 }}
        rowsPerPageOptions={[10, 20, 50, 100, 200]}
        isMultipleSelect
        isMultipleSearch
        columns={columns}
        pinnedColumns={{
          left: ['__check__', 'index', 'platform'],
          right: ['actions'],
        }}
        filtersList={FILTERS_LIST}
        getSelectedMultipleValue={getSelectedMultipleValue}
        getSelectedValue={getSelectedValue}
        selectedRowType={TableSelectType.SocialMediaLink}
        checkboxSelection={editMode}
        showFilterBySelect
        showSelectedRows={false}
        hideFooterSelectedRowCount={false}
        filterBySelectOptions={SEARCH_OPTIONS}
        renderAdditionalControl={
          <Grid
            container
            sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}
          >
            {editMode && (
              <EnforceButton disabled={selectedRows.length < 1} onClick={onClickEnforce} />
            )}
            {assetId && (
              <ExportButton modelId={assetId} selectedIds={selectedRows.map((item) => item.id)} />
            )}
            <Button
              onClick={handleUploadMenuOpen}
              sx={{ paddingX: '10px' }}
              variant="outlined"
              color="black"
              startIcon={<AddIcon />}
            >
              Upload
            </Button>
            <Menu
              anchorEl={uploadMenuAnchorEl}
              open={Boolean(uploadMenuAnchorEl)}
              onClose={() => setUploadMenuAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem>
                <AddButton />
              </MenuItem>
              <MenuItem>
                <UploadButton />
              </MenuItem>
            </Menu>
            {!editMode && (
              <EditButton
                onClick={() => {
                  setEditMode(true);
                }}
              />
            )}
          </Grid>
        }
        customSxForTable={{
          '& .Mui-selected': {
            backgroundColor: '#0000000D !important',
          },
          '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnSeparator': {
            display: 'none !important',
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'contents !important',
            '& svg': {
              pointerEvents: 'initial',
            },
          },
        }}
      />
      {editMode && (
        <Box display="flex" justifyContent="end" gap="14px">
          <EditMultipleSocialLink
            key="Edit multiple"
            listEdit={selectedRows}
            idSelected={selectedRowIds}
          />
          <InteractButton
            onClick={handleClickDelete}
            key="Delete"
            sx={{
              color: '#fff',
              backgroundColor: '#FF4848',
              '&:hover': {
                backgroundColor: '#b23232',
              },
            }}
            text="Delete"
            variant="contained"
            disabled={selectedRows.length < 1}
          />
          <InteractButton
            onClick={handleClickCancel}
            key="Cancel"
            sx={{
              color: '#000',
              border: '1px solid #000',
              '&:hover': {
                border: '1px solid #A3A7B0',
                opacity: 0.8,
              },
            }}
            text="Cancel"
            variant="outlined"
            disabled={editSocialMediaLinksMutation.isLoading}
          />
        </Box>
      )}
      <ConfirmModal
        open={open}
        onCancel={handleCancelConfirmModal}
        onSubmit={onSubmit}
        icon={isDeleting ? <DeleteModalIcon width={55} height={71} /> : undefined}
        title={
          <Typography
            sx={{
              fontSize: '24px',
              lineHeight: '20px',
              color: '#464F6080',
              whiteSpace: 'normal',
              marginBottom: '-8px',
            }}
          >
            {isDeleting
              ? `Are you sure you want to delete ${
                  selectedRowIds.length === 1 ? 'this item' : `${selectedRowIds.length} items`
                }?`
              : 'If you exit the edit mode, your current changes will be discarded.'}
          </Typography>
        }
        submitText="OK"
        usingModalV2
        sxButtonCancel={{ ...sxButtonConfirmModal }}
        submitButton={
          isDeleting ? (
            <LoadingButton
              sx={{ ...sxButtonConfirmModal }}
              size="small"
              variant="contained"
              color="error"
              onClick={onSubmit}
              loading={deleteSocialMediaLinksMutation.isLoading}
            >
              Delete
            </LoadingButton>
          ) : undefined
        }
      />

      <EditNoteModal
        open={openNoteModal}
        onCancel={() => setOpenNoteModal(false)}
        onSubmit={(newNote) => {
          handleNoteUpdate(newNote);
          setOpenNoteModal(false);
        }}
        note={editingNote}
      />
      <ConfirmEnforceModal
        open={openEnforceModal}
        onCancel={() => setOpenEnforceModal(false)}
        onConfirm={() => handleEnforce()}
      />
      <AutoSubmissionModal
        open={isOpenAutoSubmissionModal}
        links={autoSubmissionLinks}
        isEnforcing={enforceSocialMediaMutation.isLoading}
        onClose={closeAutoSubmissionModal}
        onEnforce={handleEnforce}
        onSuccess={clearData}
      />
      <SocialMediaHistoryModal
        modelId={selectedModel.modelId}
        id={selectedModel.id}
        open={openHistoryModal}
        onCancel={() => {
          setOpenHistoryModal(false);
          setSelectedModel({ modelId: '', id: '' });
        }}
      />
      <UploadEvidenceModal
        open={isOpenEvidenceModal}
        onCancel={() => {
          setIsOpenEvidenceModal(false);
          setSelectedModel({ modelId: '', id: '' });
        }}
        id={selectedModel.id}
        setId={(id) => setSelectedModel({ modelId: id, id })}
      />
    </>
  );
};
export default React.memo(SocialMediaList);
